import { isNil } from "lodash";
import { object, string, number, boolean, date, ref, array, mixed } from "yup";

import { OptionType } from "@covertree/types";
import { emailOptional, optionalPhoneNumber } from "@covertree/utils";
import { HomeType } from "@graphql/api";

export interface CoverageFormData {
  insuranceCarrierName?: Omit<OptionType, "icon">;
  isOtherInsuranceCarrier?: boolean;
  otherInsuranceCarrierName?: string;
  policyId: string;
  description?: string;
  policyEffectiveDate: Date;
  policyEndDate: Date;
  annualCost: number;
  agencyName?: string;
  agencyAddress?: string;
  agencyPhoneNumber?: string;
  agencyEmail?: string;
  isInternalSale?: boolean;
  withHomeDetails: boolean;
  occupancyType?: string;
  manufacturer?: Omit<OptionType, "icon">;
  homeType?: HomeType;
  modelYear?: Omit<OptionType, "icon">;
  personalLiabilityLimit?: number;
  homeCoverageLimit?: number;
  otherStructuresLimit?: number;
  personalPropertyLimit?: number;
  standardDeductibleLimit?: number;
  animalLiabilityLimit?: number;
  medicalPaymentsLimit?: number;
}

export interface CoverageFilesFormData {
  files: File[];
  description?: string;
}

export const coverageDataFormDefaultValues: CoverageFormData = {
  insuranceCarrierName: undefined,
  isOtherInsuranceCarrier: false,
  otherInsuranceCarrierName: undefined,
  policyId: "",
  description: undefined,
  policyEffectiveDate: undefined,
  policyEndDate: undefined,
  annualCost: undefined,
  agencyName: undefined,
  isInternalSale: undefined,
  withHomeDetails: false,
  occupancyType: "",
  manufacturer: null,
  homeType: undefined,
  modelYear: null,
  personalLiabilityLimit: undefined,
  homeCoverageLimit: undefined,
  otherStructuresLimit: undefined,
  personalPropertyLimit: undefined,
  standardDeductibleLimit: undefined,
  animalLiabilityLimit: undefined,
  medicalPaymentsLimit: undefined,
};

export const coverageFilesFormDefaultValues: CoverageFilesFormData = {
  files: [],
  description: undefined,
};

const validateOptionalCoverageLimit = () =>
  number().when("withHomeDetails", {
    is: true,
    then: (schema) =>
      schema
        .typeError("Value must be a number.")
        .positive("Value must be a positive number.")
        .optional()
        .nullable()
        .transform((_, val) => (val === "" || isNil(val) ? null : Number(val))),
    otherwise: (schema) =>
      schema
        .optional()
        .nullable()
        .transform((_, val) => (val === "" || isNil(val) ? null : Number(val))),
  });

export const validationSchemaForCoverageDataForm = object({
  isOtherInsuranceCarrier: boolean(),
  insuranceCarrierName: object({
    label: string(),
    value: string(),
  }).when("isOtherInsuranceCarrier", {
    is: false,
    then: (schema) =>
      schema.required("Insurance Carrier Name is required.").test({
        message: "Insurance Carrier Name is required.",
        test: ({ value }) => !!value,
      }),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  otherInsuranceCarrierName: string().when("isOtherInsuranceCarrier", {
    is: true,
    then: (schema) =>
      schema
        .required("Other Insurance Carrier Name is required.")
        .max(100, "Other Insurance Carrier Name is too long."),
    otherwise: (schema) => schema.optional(),
  }),
  policyId: string()
    .required("Policy ID is required.")
    .max(100, "Policy ID is too long."),
  description: string().optional().max(500, "Description is too long."),
  policyEffectiveDate: date()
    .min(
      new Date(new Date().setFullYear(new Date().getFullYear() - 5, 0, 1)),
      "Policy Effective Date cannot be more than 5 years in the past."
    )
    .max(
      new Date(new Date().setFullYear(new Date().getFullYear() + 5, 11, 31)),
      "Policy Effective Date cannot be more than 5 years in the future."
    )
    .typeError("Policy Effective Date must be a date.")
    .required("Policy Effective Date is required."),
  policyEndDate: date()
    .typeError("Policy End Date must be a date.")
    .required("Policy End Date is required.")
    .min(
      ref("policyEffectiveDate"),
      "Policy end date must be after effective date."
    )
    .max(
      new Date(new Date().setFullYear(new Date().getFullYear() + 5, 11, 31)),
      "Policy end date cannot be more than 5 years in the future.."
    ),
  annualCost: number()
    .positive("Annual Cost must be greater than $0.")
    .required("Annual Cost is required.")
    .typeError("Annual Cost is required."),
  agencyName: string().optional().max(100, "Agency name is too long."),
  agencyAddress: string().optional().max(100, "Agency address is too long."),
  agencyPhoneNumber: optionalPhoneNumber(),
  agencyEmail: emailOptional(),
  withHomeDetails: boolean(),
  occupancyType: string().when("withHomeDetails", {
    is: true,
    then: (schema) => schema.required("Occupancy Type is required."),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  manufacturer: object().when("withHomeDetails", {
    is: true,
    then: () =>
      object({
        label: string().optional(),
        value: string().optional(),
      })
        .optional()
        .nullable()
        .default(null),
    otherwise: () => mixed().optional().nullable(),
  }),
  homeType: string().optional().nullable(),
  modelYear: object().when("withHomeDetails", {
    is: true,
    then: () =>
      object({
        label: string().optional(),
        value: string().optional(),
      })
        .optional()
        .nullable()
        .default(null),
    otherwise: () => mixed().optional().nullable(),
  }),
  personalLiabilityLimit: number().when("withHomeDetails", {
    is: true,
    then: (schema) =>
      schema
        .typeError("Personal/Premises Liability Limit is required.")
        .positive("Personal/Premises Liability Limit must be greater than $0.")
        .required("Personal/Premises Liability Limit is required."),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  homeCoverageLimit: validateOptionalCoverageLimit(),
  otherStructuresLimit: validateOptionalCoverageLimit(),
  personalPropertyLimit: validateOptionalCoverageLimit(),
  standardDeductibleLimit: validateOptionalCoverageLimit(),
  animalLiabilityLimit: validateOptionalCoverageLimit(),
  medicalPaymentsLimit: validateOptionalCoverageLimit(),
});

export const createValidationSchemaForUploadProofOfCoverageDataForm = () =>
  object({
    description: string().optional().max(300, "Description is too long."),
    files: array(),
  });
